import { useEffect, useMemo, useState } from "react";
import type { FormField } from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useFormat } from "../../context/FormatContext";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import AdminDataModal from "../components/AdminDataModal";
import DataTable from "../components/DataTable";
import type { Column } from "../components/DataTable";
import "../styles/adminOrders.css";

interface OrderItem {
  id: number;
  orderItemId?: number;
  barCode?: string;
  productName: string;
  image_path?: string;
  color_name: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

interface DeliveryDetails {
  receiverName?: string;
  receiverEmail?: string;
  receiverPhone?: string;
  deliveryAddress?: string;
}

interface OrderRow {
  id: number;
  orderId: string;
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  totalAmount: number;
  currency: string;
  paymentMethod: string;
  paymentStatus: string;
  orderStatus: string;
  createdAt: string;
  updatedAt: string;
  deliveryDetails?: DeliveryDetails;
  items: OrderItem[];
}

export default function AdminOrders() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const IMAGE_BASE = import.meta.env.VITE_LYRAZONE_IMAGE_URL;
  const { showModal } = useMessageModal();
  const { formatDateToDMY, formatCurrency } = useFormat();

  const [data, setData] = useState<OrderRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<OrderRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"view" | "edit">("view");
  const [activeRow, setActiveRow] = useState<OrderRow | null>(null);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const toLabel = (value: string) =>
    String(value || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const orderFields: FormField[] = useMemo(() => {
    const editableFields = [
      "orderStatus",
      "receiverName",
      "receiverPhone",
      "deliveryAddress",
    ];

    return [
      { key: "orderId", label: "Order #", colSpan: 1 as const },
      { key: "buyerName", label: "Buyer Name", colSpan: 1 as const },
      { key: "buyerEmail", label: "Buyer Email", colSpan: 1 as const },
      { key: "buyerPhone", label: "Buyer Phone", colSpan: 1 as const },

      {
        key: "paymentMethod",
        label: "Payment Method",
        colSpan: 1 as const,
      },
      {
        key: "paymentStatus",
        label: "Payment Status",
        colSpan: 1 as const,
      },
      {
        key: "totalAmount",
        label: "Amount",
        colSpan: 1 as const,
      },
      {
        key: "createdAt",
        label: "Created At",
        colSpan: 1 as const,
      },

      {
        key: "orderStatus",
        label: "Order Status",
        type: "select" as const,
        options: [
          { label: "To Pay", value: "to_pay" },
          { label: "To Ship", value: "to_ship" },
          { label: "To Receive", value: "to_receive" },
          { label: "Completed", value: "completed" },
          { label: "Cancelled", value: "cancelled" },
          { label: "Return Refund", value: "return_refund" },
        ],
        colSpan: 2 as const,
      },

      {
        key: "receiverName",
        label: "Receiver Name",
        colSpan: 1 as const,
      },
      {
        key: "receiverEmail",
        label: "Receiver Email",
        colSpan: 1 as const,
      },
      {
        key: "receiverPhone",
        label: "Receiver Phone",
        colSpan: 1 as const,
      },
      {
        key: "deliveryAddress",
        label: "Delivery Address",
        type: "textarea" as const,
        colSpan: 2 as const,
      },
    ].map((field) => ({
      ...field,
      disabled:
        modalMode === "view" ? true : !editableFields.includes(field.key),
    }));
  }, [modalMode]);

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${BASE}/admin/orders`, {
        credentials: "include",
      });
      const result = await res.json();
      const freshData = result.data || [];

      if (result?.success) {
        setData(freshData || []);
        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("fetchOrders error:", error);
      setData([]);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const toggleExpanded = (orderId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const buildModalRowData = (row: OrderRow) => ({
    ...row,
    totalAmount: formatCurrency(row.totalAmount),
    createdAt: row.createdAt ? new Date(row.createdAt).toLocaleString() : "",
    paymentStatus: toLabel(row.paymentStatus),
    paymentMethod: row.paymentMethod,
    orderStatus: row.orderStatus,
    receiverName: row.deliveryDetails?.receiverName || row.buyerName || "",
    receiverEmail: row.deliveryDetails?.receiverEmail || row.buyerEmail || "",
    receiverPhone: row.deliveryDetails?.receiverPhone || row.buyerPhone || "",
    deliveryAddress: row.deliveryDetails?.deliveryAddress || "",
  });

  const handleView = (row: OrderRow) => {
    setModalMode("view");
    setActiveRow(row);
    setModalOpen(true);
  };

  const handleEdit = (row: OrderRow) => {
    setModalMode("edit");
    setActiveRow(row);
    setModalOpen(true);
  };

  const handleSubmit = async (formData: any) => {
    if (!activeRow) return;

    try {
      const res = await fetch(
        `${BASE}/admin/orders/${activeRow.orderId}/status`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            receiverEmail: formData.buyerEmail,
            orderStatus: formData.orderStatus,
            receiverName: formData.receiverName,
            receiverPhone: formData.receiverPhone,
            deliveryAddress: formData.deliveryAddress,
          }),
        },
      );

      const result = await res.json();

      if (result.success) {
        await fetchOrders();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message: "Order status updated successfully.",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Unable to update order status.",
        });
      }
    } catch (error) {
      console.error("handleSubmit error:", error);
      showModal({
        type: "error",
        title: "Error",
        message: "Unable to update order status.",
      });
    }
  };

  const renderStatusBadge = (status: string) => {
    const normalized = String(status || "").toLowerCase();
    const className =
      normalized === "completed"
        ? "badge-success"
        : normalized === "cancelled"
          ? "badge-danger"
          : normalized === "to_receive"
            ? "badge-info"
            : normalized === "to_ship"
              ? "badge-warning"
              : normalized === "to_pay"
                ? "badge-danger"
                : "badge-secondary";

    return <span className={className}>{toLabel(status)}</span>;
  };

  const orderSearchColumns: Column[] = [
    { key: "orderId", label: "Order #" },
    { key: "buyerName", label: "Customer" },
    { key: "paymentMethod", label: "Payment Method" },
    { key: "paymentStatus", label: "Payment Status" },
    { key: "totalAmount", label: "Amount" },
    { key: "orderStatus", label: "Order Status" },
    { key: "createdAt", label: "Created" },
  ];

  const orderTableColumns: Column[] = [
    {
      key: "expand",
      label: "",
      filterable: false,
      render: (row: OrderRow) => {
        const expanded = !!expandedRows[row.orderId];

        return (
          <button
            type="button"
            className="admin-orders-expand-btn"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(row.orderId);
            }}
          >
            {expanded ? "-" : "+"}
          </button>
        );
      },
    },
    { key: "orderId", label: "Order #" },
    { key: "buyerName", label: "Customer" },
    { key: "paymentMethod", label: "Payment Method" },
    {
      key: "paymentStatus",
      label: "Payment Status",
      render: (row: OrderRow) => renderStatusBadge(row.paymentStatus),
    },
    {
      key: "totalAmount",
      label: "Amount",
      render: (row: OrderRow) => formatCurrency(row.totalAmount),
    },
    {
      key: "orderStatus",
      label: "Order Status",
      render: (row: OrderRow) => renderStatusBadge(row.orderStatus),
    },
    {
      key: "createdAt",
      label: "Created",
      render: (row: OrderRow) =>
        row.createdAt ? formatDateToDMY(row.createdAt) : "",
    },
  ];

  return (
    <div>
      <AdminListToolkit
        title="Orders"
        columns={orderSearchColumns}
        data={data}
        selectedRow={selectedRow}
        onView={(row) => handleView(row as OrderRow)}
        onEdit={(row) => handleEdit(row as OrderRow)}
        enableExport
        exportFileName="orders"
      >
        {(filteredData: OrderRow[]) => (
          <div className="admin-orders-table-wrap">
            <DataTable
              columns={orderTableColumns}
              data={filteredData}
              selectedRowId={selectedRow?.id}
              onRowSelect={(row) => setSelectedRow(row as OrderRow)}
              getRowKey={(row) => row.orderId}
              getRowClassName={(row) =>
                selectedRow?.orderId === row.orderId
                  ? "admin-orders-parent-row selected"
                  : "admin-orders-parent-row"
              }
              isRowExpanded={(row) => !!expandedRows[row.orderId]}
              expandedRowClassName="admin-orders-children-row"
              renderExpandedRow={(row: OrderRow) => (
                <div className="admin-orders-children-box">
                  <div className="admin-orders-delivery">
                    <h4>Delivery Details</h4>

                    <p>
                      <strong>Name:</strong>{" "}
                      {row.deliveryDetails?.receiverName || row.buyerName}
                    </p>

                    <p>
                      <strong>Email:</strong>{" "}
                      {row.deliveryDetails?.receiverEmail || row.buyerEmail}
                    </p>

                    <p>
                      <strong>Phone:</strong>{" "}
                      {row.deliveryDetails?.receiverPhone || row.buyerPhone}
                    </p>

                    <p>
                      <strong>Address:</strong>{" "}
                      {row.deliveryDetails?.deliveryAddress || "-"}
                    </p>
                  </div>

                  <div className="admin-orders-items">
                    <h4>Order Items</h4>

                    {row.items?.length ? (
                      row.items.map((item) => (
                        <div
                          className="admin-orders-item-card"
                          key={item.orderItemId || `${row.orderId}-${item.id}`}
                        >
                          <div className="admin-orders-item-image">
                            {item.image_path ? (
                              <img
                                src={`${IMAGE_BASE}/uploads/products/${item.id}/${item.image_path}`}
                                alt={item.productName}
                              />
                            ) : (
                              <div className="admin-orders-item-placeholder">
                                No Image
                              </div>
                            )}
                          </div>

                          <div className="admin-orders-item-info">
                            <div className="admin-orders-item-title">
                              {item.productName}
                            </div>

                            <div className="admin-orders-item-meta">
                              BarCode: {item.barCode || "-"}
                            </div>

                            <div className="admin-orders-item-meta">
                              Variant: {item.color_name || "-"}
                            </div>

                            <div className="admin-orders-item-meta">
                              Qty: {item.quantity}
                            </div>

                            <div className="admin-orders-item-meta">
                              Unit Price: {formatCurrency(item.unitPrice)}
                            </div>

                            <div className="admin-orders-item-meta">
                              Line Total: {formatCurrency(item.lineTotal)}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p>No items available.</p>
                    )}
                  </div>
                </div>
              )}
            />
          </div>
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Order"
        fields={orderFields}
        rowData={activeRow ? buildModalRowData(activeRow) : null}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        size="xl"
      />
    </div>
  );
}
